import {matchPath} from "react-router-dom-v5-compat";

const PARENT_KHANMIGO_TOOLS_PATH: string = "/parent/khanmigo-tools";
const TEACHER_KHANMIGO_TOOLS_PATH: string = "/teacher/khanmigo-tools";
const ADMIN_KHANMIGO_TOOLS_PATH: string = "/districts/khanmigo-activities";

// sync-start:parent-tools 1671620333 https://github.com/Khan/webapp/blob/master/javascript/coach-dashboard-package/khanmigo/ai-tools-list.ts
const parentTools = {
    "assign-multiple-choice-practice":
        "teacher-tool-assign-multiple-choice-practice",
    "blooket-generator": "teacher-tool-blooket-generator",
    "chunk-text": "teacher-tool-chunk-text",
    "class-newsletter": "teacher-tool-class-newsletter",
    "class-snapshot": "activity-class-snapshot",
    "clear-directions": "teacher-tool-clear-directions",
    "discussion-prompts": "teacher-tool-discussion-prompts",
    "exit-ticket": "teacher-tool-exit-ticket",
    "fun-class-summary-poem": "teacher-tool-fun-class-summary-poem",
    "iep-assistant": "teacher-tool-iep-assistant",
    "informational-text": "teacher-tool-informational-text",
    "learning-objectives": "teacher-tool-learning-objectives",
    "lesson-hook": "teacher-tool-lesson-hook",
    "lesson-plan": "teacher-tool-create-lesson-plan",
    "letter-of-recommendation": "teacher-tool-letter-of-recommendation",
    leveler: "teacher-tool-leveler",
    "make-it-relevant": "teacher-tool-make-it-relevant",
    "multiple-choice-quiz": "teacher-tool-multiple-choice-quiz",
    "questions-generator": "teacher-tool-questions-generator",
    "real-world-context-generator": "teacher-tool-real-world-context-generator",
    "recommend-assignments": "activity-assignment-recommendations",
    "refresh-my-knowledge": "activity-refresh-my-knowledge",
    "report-card-comments": "teacher-tool-report-card-comments",
    "rubric-generator": "activity-create-a-rubric",
    "smart-goal-writer": "teacher-tool-smart-goal-writer",
    "summarize-student-chat-history": "activity-get-chat-summary-for-teacher",
    "text-rewriter": "teacher-tool-text-rewriter",
    "chat-with-gpt": "activity-chat-with-gpt",
    "refresh-my-knowledge-for-parent":
        "activity-refresh-my-knowledge-for-parent",
    "summarize-child-chat-history-for-parent":
        "activity-get-chat-summary-for-parent",
    "chat-with-gpt-for-parent": "activity-chat-with-gpt-for-parent",
    "class-snapshot-parent": "activity-class-snapshot-for-parent",
    "recommend-assignments-parent":
        "activity-assignment-recommendations-for-parent",
} as const;
// sync-end:parent-tools

// sync-start:teacher-tools 193092056 https://github.com/Khan/webapp/blob/master/javascript/coach-dashboard-package/khanmigo/ai-tools-list.ts
const teacherTools = {
    "assign-multiple-choice-practice":
        "teacher-tool-assign-multiple-choice-practice",
    "blooket-generator": "teacher-tool-blooket-generator",
    "chunk-text": "teacher-tool-chunk-text",
    "class-newsletter": "teacher-tool-class-newsletter",
    "class-snapshot": "activity-class-snapshot",
    "clear-directions": "teacher-tool-clear-directions",
    "discussion-prompts": "teacher-tool-discussion-prompts",
    "exit-ticket": "teacher-tool-exit-ticket",
    "fun-class-summary-poem": "teacher-tool-fun-class-summary-poem",
    "iep-assistant": "teacher-tool-iep-assistant",
    "informational-text": "teacher-tool-informational-text",
    "learning-objectives": "teacher-tool-learning-objectives",
    "lesson-hook": "teacher-tool-lesson-hook",
    "lesson-plan": "teacher-tool-create-lesson-plan",
    "letter-of-recommendation": "teacher-tool-letter-of-recommendation",
    leveler: "teacher-tool-leveler",
    "make-it-relevant": "teacher-tool-make-it-relevant",
    "multiple-choice-quiz": "teacher-tool-multiple-choice-quiz",
    "questions-generator": "teacher-tool-questions-generator",
    "real-world-context-generator": "teacher-tool-real-world-context-generator",
    "recommend-assignments": "activity-assignment-recommendations",
    "refresh-my-knowledge": "activity-refresh-my-knowledge",
    "report-card-comments": "teacher-tool-report-card-comments",
    "rubric-generator": "activity-create-a-rubric",
    "smart-goal-writer": "teacher-tool-smart-goal-writer",
    "summarize-student-chat-history": "activity-get-chat-summary-for-teacher",
    "text-rewriter": "teacher-tool-text-rewriter",
    "chat-with-gpt": "activity-chat-with-gpt",
} as const;
// sync-end:teacher-tools

// sync-start:admin-tools 1975295783 https://github.com/Khan/webapp/blob/master/javascript/coach-dashboard-package/khanmigo/ai-tools-list.ts
const adminTools = {
    "chat-with-gpt": "admin-tool-chat-with-gpt",
    "smart-goal-writer": "admin-tool-smart-goal-writer",
    "refresh-my-knowledge": "admin-tool-refresh-my-knowledge",
    "letter-of-recommendation": "admin-tool-letter-of-recommendation",
    "iep-assistant": "admin-tool-iep-assistant",
    "school-newsletter": "admin-school-newsletter",
    "course-snapshot": "admin-course-snapshot",
} as const;
// sync-end:admin-tools

/**
 * Determine if a path represents a Khanmigo parent tool page
 */
export const isParentToolPage = (pathname: string): boolean => {
    return !!getParentToolFromPath(pathname);
};

/**
 * Extracts the parent tool slug from a given pathname, if the path matches
 * the expected route (/parent/khanmigo-tools/:tool). If not, returns null.
 */
export const getParentToolFromPath = (pathname: string): string | null => {
    const match = matchPath(`${PARENT_KHANMIGO_TOOLS_PATH}/:tool`, pathname);

    const slug = match?.params.tool;
    if (!slug) {
        return null;
    }

    return parentTools[slug] ?? null;
};

export const isAdminToolPage = (pathname: string): boolean => {
    return !!getAdminToolFromPath(pathname);
};

export const getAdminToolFromPath = (pathname: string): string | null => {
    const match = matchPath(`${ADMIN_KHANMIGO_TOOLS_PATH}/:tool`, pathname);

    const slug = match?.params.tool;
    if (!slug) {
        return null;
    }

    return adminTools[slug] ?? null;
};

/**
 * Determine if a path represents a Khanmigo teacher tool page
 */
export const isTeacherToolPage = (pathname: string): boolean => {
    return !!getTeacherToolFromPath(pathname);
};

export const getTeacherToolFromPath = (pathname: string): string | null => {
    const match = matchPath(`${TEACHER_KHANMIGO_TOOLS_PATH}/:tool`, pathname);

    const slug = match?.params.tool;
    if (!slug) {
        return null;
    }

    return teacherTools[slug] ?? null;
};
